/**
 * Motion tokens — the single source of truth for Enough's motion language.
 *
 * The brand is calm: smooth settles, no bounce, a touch slower than typical UI.
 * Overshoot is rationed — at most ONE element per screen gets `settle`/`hero`.
 *
 * Usage:
 *   import { motion } from 'motion/react';
 *   import { useReduced, riseIn, staggerParent } from '@/lib/motion';
 *
 *   const reduced = useReduced();
 *   <motion.div variants={staggerParent(reduced)} initial="hidden" animate="show">
 *     <motion.h1 variants={riseIn(reduced)} />
 *   </motion.div>
 *
 * Rules (see CLAUDE.md → Animations):
 *  - Import from `motion/react`, never `framer-motion`.
 *  - Animate transform + opacity ONLY (GPU-friendly; the Capacitor webview needs it).
 *  - Pull every easing / spring / duration / stagger from here — no ad-hoc numbers.
 *  - Honor reduced motion everywhere via `useReduced()` + the variant factories below.
 */
import { useReducedMotion } from 'motion/react';
import type { Variants } from 'motion/react';

// ── Easing — two curves do almost everything ───────────────────────────────
export const ease = {
  calm: [0.22, 1, 0.36, 1], // smooth deceleration, NO overshoot — the default
  settle: [0.34, 1.32, 0.5, 1], // the gentlest overshoot — hero moments only
} as const;

// ── Springs ────────────────────────────────────────────────────────────────
export const spring = {
  settle: { type: 'spring', stiffness: 130, damping: 26 } as const, // basically no bounce
  hero: { type: 'spring', stiffness: 120, damping: 18 } as const, // barely-there overshoot
} as const;

// ── Durations (seconds) — calm runs slower than typical UI ───────────────────
export const dur = { quick: 0.25, base: 0.4, slow: 0.65, ambient: 0.9 } as const;

// ── Stagger steps between children ───────────────────────────────────────────
export const stagger = { tight: 0.05, base: 0.09, loose: 0.13 } as const;

// ── Ambient loop durations (seconds) — continuous background motion, NOT
//    entrances. Slow + unhurried; pair with `repeat: Infinity` + easeInOut. ──
export const loop = { breathe: 4, slow: 9, slower: 13 } as const;

/**
 * Reduced-motion hook. Wraps Motion's `useReducedMotion` so screens import one
 * thing. When true, pass it to the variant factories and they collapse to the
 * final state (opacity 1, no transform) with zero transition.
 */
export function useReduced(): boolean {
  return useReducedMotion() ?? false;
}

// ── Variant factories ────────────────────────────────────────────────────────
// Each takes the `reduced` flag first so a screen reads `variants={riseIn(reduced)}`
// and never redefines keyframes or hard-codes a magic number.

const SETTLED = { opacity: 1, y: 0, x: 0, scale: 1 };

/** Options for the variant factories. `delay` sequences a timeline; all values
 *  stay token-derived so screens never hard-code magic numbers. */
export interface VariantOpts {
  /** Seconds to wait before this element animates — for sequencing a timeline. */
  delay?: number;
  /** Travel distance in px for the rise (riseIn only). */
  distance?: number;
}

/** Rise up and fade in — the default entrance for text, rows, cards. */
export function riseIn(reduced = false, opts: VariantOpts = {}): Variants {
  const { delay = 0, distance = 16 } = opts;
  if (reduced) return { hidden: SETTLED, show: SETTLED };
  return {
    hidden: { opacity: 0, y: distance },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: dur.base, ease: ease.calm, delay },
    },
  };
}

/** Lift in — like riseIn but with a subtle scale, for cards/CTAs that "arrive". */
export function lift(reduced = false, opts: VariantOpts = {}): Variants {
  const { delay = 0 } = opts;
  if (reduced) return { hidden: SETTLED, show: SETTLED };
  return {
    hidden: { opacity: 0, y: 20, scale: 0.98 },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: dur.slow, ease: ease.calm, delay },
    },
  };
}

/**
 * Pop — the single rationed hero entrance (hub, shield, final mark). Uses the
 * gentle `settle` spring so it overshoots a hair. Use at most once per screen.
 */
export function pop(reduced = false, opts: VariantOpts = {}): Variants {
  const { delay = 0 } = opts;
  if (reduced) return { hidden: SETTLED, show: SETTLED };
  return {
    hidden: { opacity: 0, scale: 0.6 },
    show: {
      opacity: 1,
      scale: 1,
      transition: { ...spring.settle, delay },
    },
  };
}

/**
 * Stagger container — children with `hidden`/`show` variants arrive in sequence.
 * Put this on the parent and `riseIn`/`lift` on each child.
 */
export function staggerParent(
  reduced = false,
  step: number = stagger.base,
  delayChildren = 0,
): Variants {
  return {
    hidden: {},
    show: {
      transition: reduced ? {} : { staggerChildren: step, delayChildren },
    },
  };
}

/**
 * Page/step transition for a flow where one screen swaps to the next. Use with
 * `<AnimatePresence mode="wait">` and a stable key per step — a subtle slide + fade,
 * consistent on enter and exit, so a multi-screen flow reads as one continuous
 * motion instead of disconnected screens. Keep the chrome (progress bar, nav)
 * OUTSIDE AnimatePresence so only the content moves. Spread the result onto each
 * step's `motion.div`. Honors reduced motion (plain crossfade, no travel).
 */
export function pageTransition(reduced = false) {
  if (reduced) {
    return {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      exit: { opacity: 0 },
      transition: { duration: 0 },
    };
  }
  return {
    initial: { opacity: 0, x: 18 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -18 },
    transition: { duration: dur.base, ease: ease.calm },
  };
}

/**
 * Drawn-stroke transition for SVG rings/strokes animated via `pathLength`.
 * Pair with `<motion.path initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} />`.
 * Returns a final-state-friendly transition; when reduced, the stroke is instant.
 */
export function drawStroke(reduced = false, duration: number = dur.ambient) {
  return reduced
    ? { duration: 0 }
    : { duration, ease: ease.calm };
}
